/*
 * Server queries and mutations (area A). Pattern for every area:
 *   - a `xKeys` object of query keys (all → lists → list(params) → detail(id)),
 *   - `xQuery(...)` returning queryOptions (reusable for prefetching and `useSuspenseQuery`),
 *   - a `useX(...)` hook per read; mutations live next to them as `useCreateX`, `useUpdateX`, each
 *     invalidating the keys it changes.
 */
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  ActivityEvent,
  Arch,
  CancelBody,
  DeviceApproveBody,
  DeviceCode,
  InstallStepId,
  NewServer,
  OsId,
  Page,
  PlanChangeBody,
  PlanChangePreview,
  Server,
  ServerListQuery,
  ServerPatch,
  TransferBody,
} from '@/api/types'
import { i18n } from '@/i18n'
import { authKeys } from './auth'
import { notificationKeys } from './notifications'
import { subscriptionKeys } from './subscriptions'

export const serverKeys = {
  all: ['servers'] as const,
  lists: () => [...serverKeys.all, 'list'] as const,
  list: (q: ServerListQuery = {}) => [...serverKeys.lists(), q] as const,
  detail: (id: string) => [...serverKeys.all, 'detail', id] as const,
  key: (id: string) => [...serverKeys.all, 'key', id] as const,
  activity: (id: string) => [...serverKeys.all, 'activity', id] as const,
  preview: (id: string, b: PlanChangeBody) => [...serverKeys.all, 'preview', id, b] as const,
}

export const activityKeys = {
  all: ['activity'] as const,
  list: (q: { pageSize?: number } = {}) => [...activityKeys.all, 'list', q] as const,
}

export const deviceCodeKeys = {
  detail: (code: string) => ['device-codes', code] as const,
}

export const serversQuery = (q: ServerListQuery = {}) =>
  queryOptions({
    queryKey: serverKeys.list(q),
    queryFn: ({ signal }) => api.get<Page<Server>>('/servers', { query: q, signal }),
    placeholderData: keepPreviousData,
  })

export function useServers(q: ServerListQuery = {}) {
  return useQuery(serversQuery(q))
}

/** Every server of the organisation (lists and the overview work on the whole set). */
export function useAllServers() {
  return useQuery({
    ...serversQuery({ pageSize: 100, sort: 'createdAt', order: 'asc' }),
    // while one waits for activation, refresh so it flips live
    refetchInterval: (q) =>
      q.state.data?.items.some((s) => (s.licenseState === 'pending_activation' && s.install) || s.lock?.renewedAt)
        ? 3000
        : false,
  })
}

export const serverQuery = (id: string) =>
  queryOptions({
    queryKey: serverKeys.detail(id),
    queryFn: ({ signal }) => api.get<Server>(`/servers/${id}`, { signal }),
  })

/** One server. Polls every 3 s while it waits for activation, so the page flips live when it activates. */
export function useServer(id: string | undefined) {
  return useQuery({
    ...serverQuery(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const s = q.state.data
      if (!s) return false
      if (s.licenseState === 'pending_activation') return s.install ? 1500 : 3000
      if (s.keyRotation && !s.keyRotation.appliedAt) return 5000
      if (s.transfer && !s.transfer.oldDeactivatedAt) return 5000
      // renewed after a lock: unlocks at the next check-in
      if (s.lock?.renewedAt) return 4000
      return false
    },
  })
}

export function useServerActivity(id: string) {
  return useQuery({
    queryKey: serverKeys.activity(id),
    queryFn: ({ signal }) => api.get<ActivityEvent[]>(`/servers/${id}/activity`, { signal }),
  })
}

export function useActivity(pageSize = 8) {
  return useQuery({
    queryKey: activityKeys.list({ pageSize }),
    queryFn: ({ signal }) => api.get<Page<ActivityEvent>>('/activity', { query: { pageSize }, signal }),
  })
}

/** Fetches the full activation key (for SecretField's onReveal and the install commands). */
export function fetchServerKey(qc: QueryClient, id: string): Promise<string> {
  return qc
    .fetchQuery({
      queryKey: serverKeys.key(id),
      queryFn: () => api.get<{ key: string }>(`/servers/${id}/key`),
      staleTime: 60_000,
    })
    .then((r) => r.key)
}

export function useServerKey(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: serverKeys.key(id ?? ''),
    queryFn: ({ signal }) => api.get<{ key: string }>(`/servers/${id}/key`, { signal }),
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
    select: (r) => r.key,
  })
}

/** Refresh everything a server change can touch: the server, lists, subscriptions, session, bell. */
function invalidateServer(qc: QueryClient, id?: string) {
  void qc.invalidateQueries({ queryKey: serverKeys.lists() })
  if (id) {
    void qc.invalidateQueries({ queryKey: serverKeys.detail(id) })
    void qc.invalidateQueries({ queryKey: serverKeys.activity(id) })
  }
  void qc.invalidateQueries({ queryKey: activityKeys.all })
  void qc.invalidateQueries({ queryKey: subscriptionKeys.all })
  void qc.invalidateQueries({ queryKey: notificationKeys.all })
}

export function useCreateServer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NewServer) => api.post<Server>('/servers', body),
    onSuccess: async (s) => {
      qc.setQueryData(serverKeys.detail(s.id), s)
      invalidateServer(qc, s.id)
      // the session carries the server count and the trial, both can change here
      await qc.invalidateQueries({ queryKey: authKeys.session })
    },
    meta: { silent: true },
  })
}

export function useUpdateServer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ServerPatch) => api.patch<Server>(`/servers/${id}`, body),
    onSuccess: (s) => {
      qc.setQueryData(serverKeys.detail(id), s)
      invalidateServer(qc, id)
    },
    meta: { success: i18n.t('servers:toasts.saved') },
  })
}

export function useRemoveServer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/servers/${id}`),
    onSuccess: async (_d, id) => {
      qc.removeQueries({ queryKey: serverKeys.detail(id) })
      invalidateServer(qc)
      await qc.invalidateQueries({ queryKey: authKeys.session })
    },
    meta: { success: i18n.t('servers:toasts.removed') },
  })
}

export function useReissueKey(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ server: Server; key: string }>(`/servers/${id}/key/reissue`),
    onSuccess: (r) => {
      qc.setQueryData(serverKeys.detail(id), r.server)
      qc.setQueryData(serverKeys.key(id), { key: r.key })
      invalidateServer(qc, id)
    },
    meta: { success: i18n.t('servers:toasts.reissued') },
  })
}

export function usePlanPreview(id: string, body: PlanChangeBody | null) {
  return useQuery({
    queryKey: serverKeys.preview(id, body ?? { plan: 'solo', cycle: 'monthly' }),
    queryFn: ({ signal }) => api.post<PlanChangePreview>(`/servers/${id}/plan/preview`, body, { signal }),
    enabled: Boolean(body),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  })
}

export function useChangePlan(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: PlanChangeBody) =>
      api.put<{ server: Server; preview: PlanChangePreview }>(`/servers/${id}/plan`, body),
    onSuccess: (r) => {
      qc.setQueryData(serverKeys.detail(id), r.server)
      invalidateServer(qc, id)
    },
  })
}

export function useDropScheduledChange(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<Server>(`/servers/${id}/plan/scheduled`),
    onSuccess: (s) => {
      qc.setQueryData(serverKeys.detail(id), s)
      invalidateServer(qc, id)
    },
    meta: { success: i18n.t('servers:toasts.scheduledDropped') },
  })
}

export function useTransferServer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: TransferBody) =>
      api.post<{ server: Server; key: string }>(`/servers/${id}/transfer`, body),
    onSuccess: (r) => {
      qc.setQueryData(serverKeys.detail(id), r.server)
      qc.setQueryData(serverKeys.key(id), { key: r.key })
      invalidateServer(qc, id)
    },
    meta: { silent: true },
  })
}

export function useCancelServer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CancelBody) => api.post<Server>(`/servers/${id}/cancel`, body),
    onSuccess: (s) => {
      qc.setQueryData(serverKeys.detail(id), s)
      invalidateServer(qc, id)
    },
    meta: { success: i18n.t('servers:toasts.cancelled') },
  })
}

export function useResumeServer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<Server>(`/servers/${id}/resume`),
    onSuccess: (s) => {
      qc.setQueryData(serverKeys.detail(id), s)
      invalidateServer(qc, id)
    },
    meta: { success: i18n.t('servers:toasts.resumed') },
  })
}

/** Mock only: act as bpanel-install running on the server (the real installer reports by itself). */
export function useSimulateInstall(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { os?: OsId; arch?: Arch; fail?: InstallStepId }) =>
      api.post<Server>(`/_mock/servers/${id}/install`, body),
    onSuccess: (s) => {
      qc.setQueryData(serverKeys.detail(id), s)
      invalidateServer(qc, id)
    },
  })
}

export function useDeviceCode(code: string | null) {
  return useQuery({
    queryKey: deviceCodeKeys.detail(code ?? ''),
    queryFn: ({ signal }) => api.get<DeviceCode>(`/device-codes/${code}`, { signal }),
    enabled: Boolean(code),
    retry: false,
    staleTime: 0,
  })
}

export function useApproveDevice(code: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: DeviceApproveBody) =>
      api.post<{ server: Server }>(`/device-codes/${code}/approve`, body),
    onSuccess: async (r) => {
      qc.setQueryData(serverKeys.detail(r.server.id), r.server)
      invalidateServer(qc, r.server.id)
      await qc.invalidateQueries({ queryKey: authKeys.session })
    },
    meta: { silent: true },
  })
}

/** Mock only: act as an installer asking for a device code. */
export function useSimulateDeviceCode() {
  return useMutation({
    mutationFn: () => api.post<DeviceCode>('/_mock/device-codes', {}, { anonymous: true }),
  })
}
